import { ObservableDimensionsIframePresentationRenderer } from '../components/renderers/ObservableDimensionsIframePresentationRenderer';
import { ASSET_TYPE, PRESENTATION_RENDERERS, PRESENTATION_TYPE, SUPPORTED_PRESENTATION_TYPES } from '../constants/Constants';
import { ImagePresentationRenderer } from '../components/renderers/ImagePresentationRenderer';
import { IframePresentationRenderer } from '../components/renderers/IframePresentationRenderer';
import { AudioPresentationRenderer } from '../components/renderers/AudioPresentationRenderer';
import { VideoPresentationRenderer } from '../components/renderers/VideoPresentationRenderer';
import { NewWindowPresentationRenderer } from '../components/renderers/NewWindowPresentationRenderer';
import { PreviewPresentationRender } from '../components/renderers/PreviewPresentationRenderer';
import { AssetViewerPresentationRenderer } from '../components/renderers/AssetViewerPresentationRenderer';
import { ErrorPresentationRenderer } from '../components/renderers/ErrorPresentationRenderer';
import { PdfJsPresentationRenderer } from '../components/renderers/PdfJsPresentationRenderer';
import { UrlPresentationRenderer } from '../components/renderers/UrlPresentationRenderer';
import { ThreeJsPresentationRenderer } from '../components/renderers/ThreeJsPresentationRenderer';
import { UrlIframePresentationRenderer } from '../components/renderers/UrlIframePresentationRenderer';
import { PdfViewerPresentationRenderer } from '../components/renderers/PdfViewerPresentationRenderer';
export class PresentationHelper {
    static parsePresentations(presentations, page = null, downloadUrl) {
        const originalPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
        if (!originalPresentation) {
            // Create an "original" presentation if the api doesn't provide one, this is the case with shares
            presentations.push(PresentationHelper.createPresentation('original', downloadUrl));
        }
        return (presentations
            .filter(presentation => presentation && SUPPORTED_PRESENTATION_TYPES.indexOf(presentation.type) > -1)
            .map(presentation => this.updatePresentation(presentation, page))
            // Filter out the null value from above
            .filter(presentation => presentation !== null));
    }
    static createPresentationRenderers(PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP, view, pdfJsLinkService) {
        const isDocument = view.assetViewerItem.type === ASSET_TYPE.DOCUMENT;
        const isPortrait = isDocument ? PresentationHelper.isDocumentPortrait(view.assetViewerItem) : false;
        const type = isPortrait && isDocument && PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP['DOCUMENT_PORTRAIT']
            ? 'DOCUMENT_PORTRAIT'
            : view.assetViewerItem.type;
        // Add Error Presentation for unknown types
        if (!PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type]) {
            PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type] = [PRESENTATION_RENDERERS.ERROR];
        }
        return PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type]
            .map(presentationRendererType => PresentationHelper.getPresentationForPresentationRenderer(presentationRendererType, view.assetViewerItem, view.config))
            .filter(([presentationRendererType, presentation]) => presentationRendererType === PRESENTATION_RENDERERS.ASSET_VIEWER ||
            presentationRendererType === PRESENTATION_RENDERERS.PDF_VIEWER
            ? true
            : !!presentation)
            .map(([presentationRendererType, presentation, previewPresentation, highQualityPreviewPresentation]) => PresentationHelper.presentationRendererFactory(presentationRendererType, view, pdfJsLinkService, presentation, previewPresentation, highQualityPreviewPresentation))
            .filter(presentationRendererInstance => !!(presentationRendererInstance !== null))
            .filter(presentationRendererInstance => presentationRendererInstance.url ||
            // When 3d models are disabled the manifest presentation is ommited, in this case the render instance should pass
            (presentationRendererInstance instanceof ThreeJsPresentationRenderer &&
                presentationRendererInstance.assetViewerView.assetViewerItem.presentations) ||
            // AssetViewerPresentationRenderer does not require a url
            presentationRendererInstance instanceof AssetViewerPresentationRenderer ||
            // PdfViewerPresentationRenderer does not require a url
            presentationRendererInstance instanceof PdfViewerPresentationRenderer);
    }
    static isDocumentPortrait(assetViewerItem) {
        var _a;
        const presentationWithDimensions = (_a = assetViewerItem.presentations) === null || _a === void 0 ? void 0 : _a.find(presentation => !!(presentation.details &&
            presentation.details.dimensions &&
            presentation.details.dimensions.width &&
            presentation.details.dimensions.height));
        if (!presentationWithDimensions) {
            // if there's no presentation with dimensions, assume portrait
            return true;
        }
        // TODO check why this 'assume square' was added
        const dimensions = presentationWithDimensions.details.dimensions || { width: 500, height: 500 }; // assume square if dimensions are missing
        const isPortrait = dimensions.width / dimensions.height < 1;
        return isPortrait;
    }
    /**
     * Does some updates to the presentation according to the type / page
     */
    static updatePresentation(presentation, page) {
        // // For per-page presentations, check if all pages are shared or just one, fix the object in that case
        // if (presentation.type === PRESENTATION_TYPE.SVG_LOCAL && typeof page === "number") {
        //     try {
        //         // When the urls are not an array, but an object (because PHP parses it that way)
        //         if (typeof presentation.urls === "object" && typeof presentation.urls.length !== "number") {
        //             presentation.urls = {}
        //             presentation.urls[page - 1] = presentation.urls[page - 1];
        //         }
        //     }
        //     catch (exception) {
        //         // invalid data, remove presentation from the presentations list
        //     }
        // }
        // If a full document has been shared, never use pdf_page_local, it might be incomplete
        if (presentation.type === PRESENTATION_TYPE.PDF_PAGE_LOCAL && page === null) {
            presentation = null;
        }
        return presentation;
    }
    static getPresentationForPresentationRenderer(presentationRendererType, assetViewerItem, config) {
        const presentations = assetViewerItem.presentations || [];
        let presentation;
        let previewPresentation;
        let highQualityPreviewPresentation;
        let originalPresentation;
        let processedPresentation;
        let manifestPresentation;
        switch (presentationRendererType) {
            case PRESENTATION_RENDERERS.AUDIO:
                presentation =
                    presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PROCESSED) ||
                        presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
                break;
            case PRESENTATION_RENDERERS.NEW_WINDOW:
            case PRESENTATION_RENDERERS.URL_IFRAME:
            case PRESENTATION_RENDERERS.IFRAME:
            case PRESENTATION_RENDERERS.OBSERVABLE_DIMENSIONS_IFRAME:
                if (assetViewerItem.type === ASSET_TYPE.WEBAPP) {
                    presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.WEBAPP);
                }
                else if (assetViewerItem.type === ASSET_TYPE.SCORM) {
                    presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.WEBAPP);
                }
                else if (assetViewerItem.type === ASSET_TYPE.PAGE) {
                    presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PAGE);
                }
                else if (assetViewerItem.type === ASSET_TYPE.URL) {
                    presentation = presentations
                        .filter(presentation => {
                        const url = presentation.url ? presentation.url : presentation.urls ? presentation.urls[0] : null;
                        return (assetViewerItem.canRenderInIframe &&
                            presentation.type === PRESENTATION_TYPE.URL &&
                            /^(https:\/\/)/.test(url));
                    })
                        .map(presentation => {
                        const url = presentation.url ? presentation.url : presentation.urls ? presentation.urls[0] : null;
                        return PresentationHelper.createPresentation(presentation.type, url);
                    })[0];
                }
                else {
                    presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
                }
                break;
            case PRESENTATION_RENDERERS.PREVIEW:
                presentation =
                    presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PREVIEW_PAGE_PROGRESSIVE_LOCAL) ||
                        presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PREVIEW_PAGE_LOCAL) ||
                        {}; // Presentation is required. Presentation without details will fallback to the previewUrl. Don't use the original presentation for previews
                break;
            case PRESENTATION_RENDERERS.PDFJS:
                previewPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PREVIEW_PAGE_PROGRESSIVE_LOCAL);
                highQualityPreviewPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PREVIEW_PAGE_LOSSLESS_PROGRESSIVE_LOCAL);
                processedPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PROCESSED);
                originalPresentation =
                    assetViewerItem.isPdf && presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
                const pagePresentation = typeof assetViewerItem.page === 'number' &&
                    !assetViewerItem.pageRanges &&
                    presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PDF_PAGE_LOCAL);
                const firstPageIndex = (pagePresentation && pagePresentation.details && pagePresentation.details.firstPageIndex) || 0;
                const pageIndex = firstPageIndex === 1 ? assetViewerItem.page : assetViewerItem.page - 1;
                const pagePresentationHasUrl = pagePresentation && pagePresentation.urls[pageIndex];
                if (pagePresentationHasUrl) {
                    pagePresentation.details = pagePresentation.details || {};
                    pagePresentation.details.firstPageIndex = 0;
                    pagePresentation.urls = [pagePresentation.urls[pageIndex]];
                }
                presentation = pagePresentationHasUrl
                    ? pagePresentation
                    : processedPresentation
                        ? processedPresentation
                        : originalPresentation;
                break;
            case PRESENTATION_RENDERERS.URL:
                presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.URL);
                break;
            case PRESENTATION_RENDERERS.IMAGE:
                presentation =
                    presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PROCESSED) ||
                        presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
                break;
            case PRESENTATION_RENDERERS.VIDEO:
                presentation =
                    presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PROCESSED) ||
                        presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
                break;
            case PRESENTATION_RENDERERS.THREEJS:
                presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.MANIFEST) || {}; // Presentation is required.
                break;
            case PRESENTATION_RENDERERS.PDF_VIEWER:
                presentation = null;
                break;
            default:
                presentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
        }
        return [presentationRendererType, presentation, previewPresentation, highQualityPreviewPresentation];
    }
    static presentationRendererFactory(rendererType, assetViewerView, pdfJsLinkService, presentation, previewPresentation, highQualityPreviewPresentation) {
        switch (rendererType) {
            case PRESENTATION_RENDERERS.PDFJS:
                return new PdfJsPresentationRenderer(assetViewerView.config, assetViewerView, pdfJsLinkService, presentation, previewPresentation, highQualityPreviewPresentation);
            case PRESENTATION_RENDERERS.IFRAME:
                return new IframePresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.OBSERVABLE_DIMENSIONS_IFRAME:
                return new ObservableDimensionsIframePresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.AUDIO:
                return new AudioPresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.VIDEO:
                return new VideoPresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.IMAGE:
                return new ImagePresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.NEW_WINDOW:
                return new NewWindowPresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.PREVIEW:
                return new PreviewPresentationRender(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.ASSET_VIEWER:
                return new AssetViewerPresentationRenderer(assetViewerView);
            case PRESENTATION_RENDERERS.ERROR:
                return new ErrorPresentationRenderer(assetViewerView);
            case PRESENTATION_RENDERERS.URL:
                return new UrlPresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.URL_IFRAME:
                return new UrlIframePresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.THREEJS:
                return new ThreeJsPresentationRenderer(assetViewerView, presentation);
            case PRESENTATION_RENDERERS.PDF_VIEWER:
                return new PdfViewerPresentationRenderer(assetViewerView);
        }
    }
    static createPresentation(type, url) {
        return {
            type: type,
            urls: [url],
            details: {
                firstPageIndex: 0,
                links: null,
                stylesheet: null,
                version: null,
                dimensions: {
                    width: null,
                    height: null,
                    exceptions: null
                }
            },
            numberOfPages: 1
        };
    }
}
//# sourceMappingURL=PresentationHelper.js.map